import type {
  TripLegResponseItem,
  TripLegAttestationItem,
} from '../data/models/trip-leg.model';
import type { TimeZone } from '../data/models/timezone.model';
import type { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { tripStatusNoLoadList } from '../data/models/trip-leg-status.model';
import { attestationUtils } from '../services/attestationUtils';
import DateUtils from '../services/DateUtils';
import { financialUtils } from '../services/financialUtils';
import {
  checkTimestampsFilled,
  checkTripLegReadyToInvoice,
} from './timestampValidation';
import type {
  OptimisticTimestamps,
  OptimisticStatuses,
} from '../hooks/useOptimisticUpdates';

/**
 * Extracts timestamp from status logs for a given status
 */
function getTimestampForStatus(
  tripLeg: TripLegResponseItem,
  statusBC: TripLegStatusBC | TripLegStatusBC[],
): string {
  if (Array.isArray(statusBC)) {
    return (
      tripLeg.tripLegStatusLogs?.find((log) =>
        statusBC.includes(log.tripLegStatusBC as TripLegStatusBC),
      )?.createdOnUTC || ''
    );
  }
  return (
    tripLeg.tripLegStatusLogs?.find(
      ({ tripLegStatusBC: bc }) => bc === statusBC,
    )?.createdOnUTC || ''
  );
}

/**
 * Gets the optimistic or backend timestamp for a field
 */
function getOptimisticOrBackendTimestamp(
  tripLeg: TripLegResponseItem,
  field: 'onSite' | 'onBoard' | 'completed' | 'noLoad' | 'canceledLate',
  optimisticTimestamps: OptimisticTimestamps,
  optimisticStatus: OptimisticStatuses[string] | undefined,
): string {
  const statusMap: Record<typeof field, TripLegStatusBC | TripLegStatusBC[]> = {
    onSite: 'ONSITE' as TripLegStatusBC,
    onBoard: 'ONBOARD' as TripLegStatusBC,
    completed: 'COMPLETED' as TripLegStatusBC,
    noLoad: tripStatusNoLoadList as TripLegStatusBC[],
    canceledLate: 'CANCELEDLATE' as TripLegStatusBC,
  };

  // Check optimistic status timestamps first
  if (optimisticStatus?.timestamps[field]) {
    return optimisticStatus.timestamps[field]!;
  }

  // Check individual optimistic timestamps
  if (optimisticTimestamps[tripLeg.tripLegReferenceID]?.[field]) {
    return optimisticTimestamps[tripLeg.tripLegReferenceID][field];
  }

  // Fall back to backend timestamp
  return getTimestampForStatus(tripLeg, statusMap[field]);
}

/**
 * Calculates the total price including add-ons
 */
function calculateTotalPrice(tripLeg: TripLegResponseItem): string {
  const baseAmount = financialUtils.parsePrice(
    (tripLeg?.payableAmount as string) ?? '0',
  );
  const addOnTotal =
    tripLeg.tripLegAddOnLineItems?.reduce((sum, addOn) => {
      return sum + financialUtils.parsePrice(addOn.lineItemAmount);
    }, 0) || 0;
  return financialUtils.formatPrice(baseAmount + addOnTotal);
}

/**
 * Gets the exception message for a trip leg
 */
export function getExceptionMessage(
  tripLeg: TripLegAttestationItem,
  t: (key: string) => string,
): string {
  if (tripLeg.isUnderDiscussion) {
    return t('needsAttention.underDiscussion');
  }

  return checkTripLegReadyToInvoice(tripLeg)
    ? t('needsAttention.readyToInvoice')
    : t('needsAttention.missingInfo');
}

/**
 * Transforms a trip leg into a row suitable for display in the data grid.
 *
 * @param tripLeg - The trip leg to transform
 * @param timezones - Available timezones for formatting
 * @param optimisticTimestamps - Optimistic timestamp updates
 * @param optimisticStatuses - Optimistic status changes
 * @param t - Translation function
 * @returns Transformed trip leg row
 */
export function transformTripLegToRow(
  tripLeg: TripLegResponseItem,
  timezones: TimeZone[],
  optimisticTimestamps: OptimisticTimestamps,
  optimisticStatuses: OptimisticStatuses,
  t: (key: string) => string,
): TripLegAttestationItem & {
  id?: number;
  pickupTime?: string;
  timezone?: string;
  tripID?: string;
  pickupAddress?: string;
  pickupCity?: string;
  pickupState?: string;
  pickupZipCode?: string;
  dropoffAddress?: string;
  dropoffCity?: string;
  dropoffState?: string;
  dropoffZipCode?: string;
  price?: string;
  isReadyToInvoice?: boolean;
  hasRequiredTimestamps?: boolean;
} {
  const sortedLogs = [...(tripLeg.tripLegStatusLogs || [])].sort(
    (a, b) =>
      new Date(b.createdOnUTC).getTime() - new Date(a.createdOnUTC).getTime(),
  );

  const canceledLateTimestamp = sortedLogs.find(
    (tripLegStatusLog) =>
      tripLegStatusLog?.tripLegStatusBC === ('CANCELEDLATE' as TripLegStatusBC),
  )?.createdOnUTC;

  const noLoadTimestamp = sortedLogs.find((tripLegStatusLog) =>
    tripStatusNoLoadList.includes(
      tripLegStatusLog?.tripLegStatusBC as TripLegStatusBC,
    ),
  )?.createdOnUTC;

  const timezone = DateUtils.getTimeZoneIana(
    timezones,
    tripLeg?.pickUpAddressTimeZoneID ?? 0,
  );

  const totalPrice = calculateTotalPrice(tripLeg);

  const optimistic = optimisticTimestamps[tripLeg.tripLegReferenceID] || {};
  const optimisticStatus = optimisticStatuses[tripLeg.tripLegReferenceID];

  const onSiteTime = getOptimisticOrBackendTimestamp(
    tripLeg,
    'onSite',
    optimisticTimestamps,
    optimisticStatus,
  );

  const onBoardTime = getOptimisticOrBackendTimestamp(
    tripLeg,
    'onBoard',
    optimisticTimestamps,
    optimisticStatus,
  );

  const completedTime = getOptimisticOrBackendTimestamp(
    tripLeg,
    'completed',
    optimisticTimestamps,
    optimisticStatus,
  );

  const noLoadTime = getOptimisticOrBackendTimestamp(
    tripLeg,
    'noLoad',
    optimisticTimestamps,
    optimisticStatus,
  );

  const canceledLateTime = getOptimisticOrBackendTimestamp(
    tripLeg,
    'canceledLate',
    optimisticTimestamps,
    optimisticStatus,
  );

  const currentStatus = optimisticStatus?.status || tripLeg.tripLegStatusBC;

  // Return the transformed trip leg with additional display fields
  return {
    ...tripLeg,
    id: tripLeg?.tripLegID ?? 0,
    pickupTime: tripLeg?.isWillCall
      ? attestationUtils.formatDate(
          tripLeg?.appointmentDate ?? '',
          timezone,
          tripLeg.isWillCall ?? false,
        )
      : attestationUtils.formatDate(
          tripLeg?.pickUpDueTimeUTC ?? '',
          timezone,
          tripLeg.isWillCall ?? false,
        ),
    timezone: timezone,
    tripID: tripLeg?.tripLegReferenceID,
    pickupAddress: [tripLeg?.pickUpAddressLine1, tripLeg?.pickUpAddressLine2]
      .filter(Boolean)
      .join(', '),
    pickupCity: tripLeg?.pickUpCity,
    pickupState: tripLeg?.pickUpStateCode,
    pickupZipCode: tripLeg?.pickUpZipCode,
    dropoffAddress: [tripLeg?.dropOffAddressLine1, tripLeg?.dropOffAddressLine2]
      .filter(Boolean)
      .join(', '),
    dropoffCity: tripLeg?.dropOffCity,
    dropoffState: tripLeg?.dropOffStateCode,
    dropoffZipCode: tripLeg?.dropOffZipCode,
    onSite: onSiteTime,
    onBoard: onBoardTime,
    completed: completedTime,
    driverName: attestationUtils.getDriverName(tripLeg),
    price: totalPrice,
    isReadyToInvoice: checkTripLegReadyToInvoice(tripLeg),
    isUnderDiscussion: !!tripLeg.isUnderDiscussion,
    exception: getExceptionMessage(tripLeg, t),
    hasRequiredTimestamps: checkTimestampsFilled(tripLeg),
    isException: tripLeg?.isException,
    tripLegExceptionBC: tripLeg.tripLegExceptionBC,
    tripLegStatusBC: currentStatus as TripLegStatusBC,
    canceledLateTimeStamp: canceledLateTime,
    noLoadTimeStamp: noLoadTime,
  };
}
